// Block K (UI-22): the Tax summary section of Invoicing, on the v2 kit (tax.css, prefix tax-).
//
// WHAT COUNTS (Block K, failing-first in TaxSummary.test.js):
//   - an invoice counts once it is issued: not a draft and not a cancelled invoice;
//   - a refund takes its share of the invoice's tax back (a fully refunded invoice adds none, a
//     partly refunded one adds the kept share). Mirrors utils/invoiceOverdue keptTotal;
//   - a rejected expense reclaims nothing;
//   - decision 72: tax in different currencies is never added together. Each figure is kept per
//     currency and shown as "£20.00 plus €20.00". Expenses carry no currency of their own, so they
//     are in the business's base currency (currentUser.baseCurrency, else GBP), as on Financials.
//
// ITEM 34 (Carla's approved launch set, 2026-10-20):
//   - PER RATE: output VAT and net for each rate (standard, reduced, zero-rated, exempt, outside the
//     scope), through the one rounding rule (lib/invoiceAmounts, per line). "Net sales" is the VAT
//     return's box 6: every sale except those outside the scope, so zero-rated and exempt are in it.
//   - input VAT from expenses, as before;
//   - the HMRC quarter stagger (1, 2 or 3) from the one VAT setting (Settings, Branding);
//   - "VAT registered" is that ONE setting too: this page's own toggle is gone.
//   - refunds unchanged for now (a refund takes its share back); credit notes come after launch.
import { useState, useEffect, useMemo } from "react";
import { invoiceAmounts } from "../../lib/invoiceAmounts";
import { useBusinessVat } from "../../lib/useBusinessVat";
import { useCurrentUser } from "../../contexts/CurrentUserContext";
import { useInvoices } from "../../contexts/InvoicesContext";
import { useExpenses } from "../../contexts/ExpensesContext";
import { getTaxConfig, TAX_CONFIGS, COUNTRIES } from "../../services/taxConfigService";
import { api } from "../../api/client";
import { Button, Modal, StatTile, Select, EmptyState, ErrorState, Skeleton, StatusPill } from "../../ui";
import { formatMoney } from "../../utils/currency";
import { isPaidStatus, invoiceDisplayStatus } from "../../utils/invoiceOverdue";
import { messageFromError } from "../../utils/apiError";
import { showErrorToast } from "../../utils/toast";
import SectionHead from "./SectionHead";
import "./invoicing.css";
import "./tax.css";

// ─── Helpers ──────────────────────────────────────────────────────────────────

function fmtDate(str) {
  if (!str) return "—";
  const d = new Date(String(str).slice(0, 10) + "T12:00:00");
  if (isNaN(d.getTime())) return "—";
  return d.toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" });
}

// The rows of the per-rate table, in this order, and the words for each.
const RATE_ROWS = [
  { key: "standard", label: "20% standard" },
  { key: "reduced", label: "5% reduced" },
  { key: "zero", label: "0% zero-rated" },
  { key: "exempt", label: "Exempt" },
  { key: "outside_scope", label: "Outside the scope" },
];
const rateRowLabel = (key, percent) => (RATE_ROWS.find(r => r.key === key) || {}).label || `${percent}%, rate not chosen`;

// An invoice's figures, per rate, less the share given back by refunds. Item 34: through the one
// rounding rule (lib/invoiceAmounts), so this page adds up the same pennies the invoices show.
export function invoiceTaxFigures(inv) {
  const a = invoiceAmounts({ lineItems: (inv && inv.lineItems) || [], currency: (inv && inv.currency) || "GBP" });
  const gross = a.total;
  let share = 1;
  if (inv && inv.status === "refunded") share = 0;
  else if (inv && isPaidStatus(inv.status) && Number(inv.refundedAmount || 0) > 0) {
    share = gross > 0 ? Math.max(0, gross - Number(inv.refundedAmount)) / gross : 0;
  }
  const byRate = a.byRate.map(g => ({ key: g.key, percent: g.percent, net: g.net * share, vat: g.vat * share }));
  const outsideScope = byRate.filter(g => g.key === "outside_scope").reduce((t, g) => t + g.net, 0);
  return {
    net: a.subtotal * share, tax: a.tax * share, gross: gross * share, share, byRate,
    // Box 6: every sale but those outside the scope (zero-rated and exempt included).
    netSales: a.subtotal * share - outsideScope, outsideScope,
  };
}

// Does this invoice belong in a tax return? Issued, not cancelled, not wholly refunded.
export function invoiceCountsForTax(inv) {
  if (!inv || !inv.issueDate) return false;
  if (inv.status === "draft" || inv.status === "cancelled") return false;
  return invoiceTaxFigures(inv).share > 0;
}

export function expenseCountsForTax(exp) {
  return !!exp && !!exp.date && (Number(exp.tax_rate) || 0) > 0 && exp.approval_status !== "rejected";
}

const expenseTax = (exp) => Number(exp.amount) * (Number(exp.tax_rate) || 0) / 100;
const invCurrency = (inv) => inv.currency || "GBP";
const day10 = (s) => String(s || "").slice(0, 10);

// Build the period bucket that a date falls into, based on filing frequency. Item 34: a quarterly
// period follows the HMRC stagger (1: quarters ending March, June, September, December; 2: April,
// July, October, January; 3: May, August, November, February).
const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
export function getPeriodKey(dateStr, filingPeriod, stagger = 1) {
  const [y, m] = day10(dateStr).split("-").map(Number);
  const mo = m - 1; // 0-indexed
  const pad = n => String(n).padStart(2, "0");

  if (filingPeriod === "monthly") {
    const lastDay = new Date(y, m, 0).getDate();
    return {
      key: `${y}-${pad(m)}`,
      label: new Date(y, mo, 1).toLocaleDateString("en-GB", { month: "long", year: "numeric" }),
      start: `${y}-${pad(m)}-01`,
      end: `${y}-${pad(m)}-${lastDay}`,
    };
  }

  if (filingPeriod === "bimonthly") {
    const bi = Math.floor(mo / 2);
    const startMo0 = bi * 2;
    const endMo0 = startMo0 + 1;
    const lastDay = new Date(y, endMo0 + 1, 0).getDate();
    const labels = ["Jan to Feb", "Mar to Apr", "May to Jun", "Jul to Aug", "Sep to Oct", "Nov to Dec"];
    return {
      key: `${y}-B${bi + 1}`,
      label: `${labels[bi]} ${y}`,
      start: `${y}-${pad(startMo0 + 1)}-01`,
      end: `${y}-${pad(endMo0 + 1)}-${lastDay}`,
    };
  }

  // Quarterly, on the stagger: quarters START in month (stagger - 1), +3, +6, +9 (0-indexed).
  const off = [1, 2, 3].includes(Number(stagger)) ? Number(stagger) - 1 : 0;
  const k = Math.floor((((mo - off) % 12) + 12) % 12 / 3);
  const startMo0 = off + 3 * k;                       // 0..11
  const startY = mo < startMo0 ? y - 1 : y;
  const endAbs = startMo0 + 2;                        // may run into the next year
  const endY = startY + Math.floor(endAbs / 12);
  const endMo0 = endAbs % 12;
  const lastDay = new Date(endY, endMo0 + 1, 0).getDate();
  return {
    key: `${startY}-${pad(startMo0 + 1)}`,
    label: off === 0 ? `Q${k + 1} ${startY}` : `${MONTHS[startMo0]} ${startY} to ${MONTHS[endMo0]} ${endY}`,
    start: `${startY}-${pad(startMo0 + 1)}-01`,
    end: `${endY}-${pad(endMo0 + 1)}-${lastDay}`,
  };
}

// byCur: { GBP: { outputTax, inputTax, taxableNet, rates } } — one bucket per currency, never
// merged. taxableNet is box 6 (net sales); rates holds { key: { percent, net, vat } } per rate.
function bucket(byCur, cur) {
  if (!byCur[cur]) byCur[cur] = { outputTax: 0, inputTax: 0, taxableNet: 0, rates: {} };
  return byCur[cur];
}
function addRates(into, byRate) {
  for (const g of byRate) {
    const r = into[g.key] || (into[g.key] = { percent: g.percent, net: 0, vat: 0 });
    r.net += g.net; r.vat += g.vat;
  }
}

function buildPeriods(invoices, expenses, taxConfig, baseCurrency, stagger) {
  const { filingPeriod, reclaimable } = taxConfig;
  const map = {};

  const ensure = p => {
    if (!map[p.key]) map[p.key] = { ...p, byCur: {}, invoiceCount: 0, expenseCount: 0 };
    return map[p.key];
  };

  invoices.forEach(inv => {
    if (!invoiceCountsForTax(inv)) return;
    const p = ensure(getPeriodKey(inv.taxPoint || inv.issueDate, filingPeriod, stagger));
    const f = invoiceTaxFigures(inv);
    const b = bucket(p.byCur, invCurrency(inv));
    b.outputTax += f.tax;
    b.taxableNet += f.netSales;
    addRates(b.rates, f.byRate);
    p.invoiceCount += 1;
  });

  if (reclaimable) {
    expenses.forEach(exp => {
      if (!expenseCountsForTax(exp)) return;
      const p = ensure(getPeriodKey(exp.date, filingPeriod, stagger));
      bucket(p.byCur, baseCurrency).inputTax += expenseTax(exp);
      p.expenseCount += 1;
    });
  }

  return Object.values(map).sort((a, b) => b.key.localeCompare(a.key));
}

function mergeByCur(periods) {
  const out = {};
  periods.forEach(p => Object.entries(p.byCur).forEach(([cur, v]) => {
    const b = bucket(out, cur);
    b.outputTax += v.outputTax; b.inputTax += v.inputTax; b.taxableNet += v.taxableNet;
    addRates(b.rates, Object.entries(v.rates || {}).map(([key, r]) => ({ key, ...r })));
  }));
  return out;
}

// The base currency first, then the others A to Z.
function orderedCurrencies(codes, base) {
  return [...codes].sort((a, b) => (a === base ? -1 : b === base ? 1 : a.localeCompare(b)));
}

// [{ cur, value }] for one figure across currencies; zeros dropped, but never an empty list.
function moneyParts(byCur, pick, base) {
  const parts = orderedCurrencies(Object.keys(byCur), base)
    .map(cur => ({ cur, value: pick(byCur[cur]) }))
    .filter(p => Math.abs(p.value) >= 0.005);
  return parts.length ? parts : [{ cur: base, value: 0 }];
}

function moneyText(byCur, pick, base) {
  return moneyParts(byCur, pick, base).map(p => formatMoney(p.value, p.cur)).join(" plus ");
}

// One currency per line in a table cell: "£20.00" then "plus €20.00".
function MoneyLines({ byCur, pick, base }) {
  return (
    <span className="tax-lines">
      {moneyParts(byCur, pick, base).map((p, i) => (
        <span key={p.cur} className="tax-lines__one">{i > 0 ? "plus " : ""}{formatMoney(p.value, p.cur)}</span>
      ))}
    </span>
  );
}

const pickOutput = b => b.outputTax;
const pickInput = b => b.inputTax;
const pickNet = b => b.outputTax - b.inputTax;
const pickTaxable = b => b.taxableNet;
const hasTax = byCur => Object.values(byCur).some(b => b.outputTax > 0 || b.inputTax > 0);

function exportCSV(periods, taxConfig, base) {
  const { taxAbbr } = taxConfig;
  const fmt = n => n.toFixed(2);
  const esc = v => `"${String(v ?? "").replace(/"/g, '""')}"`;
  const rateCols = RATE_ROWS.flatMap(r => [`${r.label} net`, `${r.label} ${taxAbbr}`]);
  const headers = ["Period", "From", "To", "Currency", "Net sales", ...rateCols, `Output ${taxAbbr}`,
    ...(taxConfig.reclaimable ? [`Input ${taxAbbr} (reclaimable)`] : []),
    `Net ${taxAbbr} payable`, "Invoices"];
  const rows = [headers.map(esc).join(",")];
  // Decision 72: one row per period PER CURRENCY, never a sum across currencies.
  periods.forEach(p => {
    orderedCurrencies(Object.keys(p.byCur), base).forEach(cur => {
      const b = p.byCur[cur];
      rows.push([
        p.label, p.start, p.end, cur, fmt(b.taxableNet),
        ...RATE_ROWS.flatMap(r => [fmt((b.rates[r.key] || {}).net || 0), fmt((b.rates[r.key] || {}).vat || 0)]),
        fmt(b.outputTax),
        ...(taxConfig.reclaimable ? [fmt(b.inputTax)] : []),
        fmt(b.outputTax - b.inputTax), p.invoiceCount,
      ].map(esc).join(","));
    });
  });
  const blob = new Blob([rows.join("\n")], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = `tax-summary-${new Date().toISOString().slice(0, 10)}.csv`;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

const STATUS_PILL = {
  paid: { tone: "success", label: "Paid" },
  partially_refunded: { tone: "neutral", label: "Partly refunded" },
  refunded: { tone: "neutral", label: "Refunded" },
  overdue: { tone: "warning", label: "Overdue" },
  pending: { tone: "pending", label: "Due" },   // fresh-eyes 24
};

// ─── Output VAT and net, per rate (item 34) ──────────────────────────────────────
function ByRateTable({ byCur, base, taxAbbr }) {
  const curs = orderedCurrencies(Object.keys(byCur), base);
  if (!curs.length) return null;
  return (
    <div className="invc-table-wrap" data-testid="tax-by-rate">
      <table className="invc-table tax-table">
        <caption className="tax-detail__title">By rate</caption>
        <thead>
          <tr><th>Rate</th>{curs.length > 1 && <th>Currency</th>}<th className="is-num">Net</th><th className="is-num">Output {taxAbbr}</th></tr>
        </thead>
        <tbody>
          {curs.flatMap(cur => {
            const rates = byCur[cur].rates || {};
            const keys = [...RATE_ROWS.map(r => r.key).filter(k => rates[k]), ...Object.keys(rates).filter(k => !RATE_ROWS.some(r => r.key === k))];
            return keys.map(k => (
              <tr key={`${cur}-${k}`}>
                <td>{rateRowLabel(k, rates[k].percent)}</td>
                {curs.length > 1 && <td>{cur}</td>}
                <td className="is-num">{formatMoney(rates[k].net, cur)}</td>
                <td className="is-num invc-money">{formatMoney(rates[k].vat, cur)}</td>
              </tr>
            ));
          })}
        </tbody>
        <tfoot>
          <tr>
            <td>Net sales (box 6: all but outside the scope)</td>
            {curs.length > 1 && <td />}
            <td className="is-num" data-testid="tax-net-sales"><MoneyLines byCur={byCur} pick={pickTaxable} base={base} /></td>
            <td className="is-num"><MoneyLines byCur={byCur} pick={pickOutput} base={base} /></td>
          </tr>
        </tfoot>
      </table>
    </div>
  );
}

// ─── Period detail modal ──────────────────────────────────────────────────────

function PeriodDetailModal({ period, invoices, expenses, taxConfig, base, onClose }) {
  const { taxName, taxAbbr, reclaimable } = taxConfig;

  const pInvoices = invoices.filter(
    inv => invoiceCountsForTax(inv) && day10(inv.issueDate) >= period.start && day10(inv.issueDate) <= period.end,
  );
  const pExpenses = reclaimable
    ? expenses.filter(exp => expenseCountsForTax(exp) && day10(exp.date) >= period.start && day10(exp.date) <= period.end)
    : [];

  // Totals of the listed invoices, per currency.
  const invTotals = {};
  pInvoices.forEach(inv => {
    const f = invoiceTaxFigures(inv);
    const t = invTotals[invCurrency(inv)] || (invTotals[invCurrency(inv)] = { net: 0, tax: 0, gross: 0 });
    t.net += f.net; t.tax += f.tax; t.gross += f.gross;
  });
  const expTotal = pExpenses.reduce((s, e) => s + expenseTax(e), 0);

  return (
    <Modal
      open
      onClose={onClose}
      title={`${taxName} detail, ${period.label}`}
      color="pastel-yellow"
      width={780}
      footer={<Button variant="tertiary" onClick={onClose}>Close</Button>}
    >
      <div className="tax-detail">
        <p className="invc-muted">{fmtDate(period.start)} to {fmtDate(period.end)}</p>

        <h4 className="tax-detail__title">Output {taxAbbr}, sales invoices</h4>
        {pInvoices.length === 0 ? (
          <p className="invc-muted">No invoices in this period.</p>
        ) : (
          <div className="invc-table-wrap">
            <table className="invc-table tax-table">
              <thead>
                <tr>
                  <th>Invoice</th><th>Date</th><th>Client</th>
                  <th className="is-num">Net</th><th className="is-num">{taxAbbr}</th><th className="is-num">Gross</th>
                  <th>Status</th>
                </tr>
              </thead>
              <tbody>
                {pInvoices.map(inv => {
                  const f = invoiceTaxFigures(inv);
                  const cur = invCurrency(inv);
                  const pill = STATUS_PILL[invoiceDisplayStatus(inv)] || STATUS_PILL.pending;
                  return (
                    <tr key={inv.id}>
                      <td>{inv.number}</td>
                      <td>{fmtDate(inv.issueDate)}</td>
                      <td className="invc-wrap">{inv.contactName || "—"}</td>
                      <td className="is-num">{formatMoney(f.net, cur)}</td>
                      <td className="is-num invc-money">{formatMoney(f.tax, cur)}</td>
                      <td className="is-num invc-money">{formatMoney(f.gross, cur)}</td>
                      <td><StatusPill tone={pill.tone}>{pill.label}</StatusPill></td>
                    </tr>
                  );
                })}
              </tbody>
              <tfoot>
                <tr>
                  <td colSpan={3}>Totals</td>
                  <td className="is-num"><MoneyLines byCur={invTotals} pick={t => t.net} base={base} /></td>
                  <td className="is-num"><MoneyLines byCur={invTotals} pick={t => t.tax} base={base} /></td>
                  <td className="is-num"><MoneyLines byCur={invTotals} pick={t => t.gross} base={base} /></td>
                  <td />
                </tr>
              </tfoot>
            </table>
          </div>
        )}

        {/* Input tax — only for reclaimable jurisdictions */}
        {reclaimable && (
          <>
            <h4 className="tax-detail__title">Input {taxAbbr}, reclaimable expenses</h4>
            {pExpenses.length === 0 ? (
              <p className="invc-muted">No {taxAbbr}-bearing expenses in this period.</p>
            ) : (
              <div className="invc-table-wrap">
                <table className="invc-table tax-table">
                  <thead>
                    <tr>
                      <th>Date</th><th>Description</th><th>Category</th>
                      <th className="is-num">Net</th><th className="is-num">Rate</th><th className="is-num">{taxAbbr}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {pExpenses.map(exp => (
                      <tr key={exp.id}>
                        <td>{fmtDate(exp.date)}</td>
                        <td className="invc-wrap">{exp.title || "—"}</td>
                        <td>{exp.category || "—"}</td>
                        <td className="is-num">{formatMoney(Number(exp.amount), base)}</td>
                        <td className="is-num">{exp.tax_rate || 0}%</td>
                        <td className="is-num invc-money">{formatMoney(expenseTax(exp), base)}</td>
                      </tr>
                    ))}
                  </tbody>
                  <tfoot>
                    <tr>
                      <td colSpan={5}>Total input {taxAbbr}</td>
                      <td className="is-num">{formatMoney(expTotal, base)}</td>
                    </tr>
                  </tfoot>
                </table>
              </div>
            )}
          </>
        )}

        {/* Net payable */}
        <div className="invc-panel tax-net">
          <div className="tax-net__text">
            <span className="tax-net__label">{reclaimable ? `Net ${taxAbbr} payable` : `${taxAbbr} collected`}</span>
            {reclaimable && (
              <span className="invc-small">
                Output {moneyText(period.byCur, pickOutput, base)} less input {moneyText(period.byCur, pickInput, base)}
              </span>
            )}
          </div>
          <span className="invc-money tax-net__value"><MoneyLines byCur={period.byCur} pick={pickNet} base={base} /></span>
        </div>
      </div>
    </Modal>
  );
}

// ─── Source badge ─────────────────────────────────────────────────────────────

const SOURCE_LABELS = {
  manual: "Manually set",
  profile: "From profile",
  timezone: "Auto-detected (timezone)",
  language: "Auto-detected (language)",
  default: "Default",
};

const FILING_WORDS = { monthly: "monthly", bimonthly: "every two months", quarterly: "quarterly" };

// ─── Main component ───────────────────────────────────────────────────────────

export default function TaxSummary() {
  const { currentUser } = useCurrentUser();
  const baseCurrency = String(currentUser?.baseCurrency || "GBP").toUpperCase();

  // Country override: persisted to user preferences so it survives refresh
  const [countryOverride, setCountryOverride] = useState("");

  // Item 34: VAT registered is the ONE business setting (Settings, Branding), read through the
  // server (lib/useBusinessVat), with its HMRC quarter stagger. This page no longer has its own.
  const { vat } = useBusinessVat();
  const vatRegistered = !!vat.registered;

  useEffect(() => {
    api('/api/users/me')
      .then(res => res.json())
      .then(data => {
        const prefs = data?.user?.preferences ?? {};
        if (prefs.taxConfigCountry) setCountryOverride(prefs.taxConfigCountry);
      })
      .catch(() => { });
  }, []);

  // Block K: a choice that did not save goes back to what is saved, and says so (it used to stay
  // on screen with only a console.error, so the next visit quietly showed the old one).
  function applyCountryOverride(code) {
    const prev = countryOverride;
    setCountryOverride(code);
    api('/api/users/me', { method: 'PUT', body: JSON.stringify({ preferences: { taxConfigCountry: code || null } }) })
      .catch(err => {
        setCountryOverride(prev);
        showErrorToast(messageFromError(err, "Your tax jurisdiction was not saved. Please try again."));
      });
  }

  const taxConfig = useMemo(() => getTaxConfig(currentUser, countryOverride || undefined), [currentUser, countryOverride]);

  const { taxName, taxAbbr, reclaimable, source, countryCode, filingPeriod } = taxConfig;

  const { invoices: allInvoices, loaded = true, loadFailed = false, refresh } = useInvoices();
  const { expenses: allExpenses } = useExpenses();

  const [yearsBack, setYearsBack] = useState(1);
  const [selectedPeriod, setSelectedPeriod] = useState(null);


  const allPeriods = useMemo(
    () => buildPeriods(allInvoices, allExpenses, taxConfig, baseCurrency, countryCode === "GB" ? vat.stagger : 1),
    [allInvoices, allExpenses, taxConfig, baseCurrency, countryCode, vat.stagger],
  );

  const cutoff = useMemo(() => {
    const d = new Date();
    d.setFullYear(d.getFullYear() - yearsBack);
    return d.toISOString().slice(0, 10);
  }, [yearsBack]);

  const visible = allPeriods.filter(p => p.end >= cutoff);
  const totals = mergeByCur(visible);
  const invoiceCount = visible.reduce((s, p) => s + p.invoiceCount, 0);

  const sourceText = SOURCE_LABELS[source] || SOURCE_LABELS.default;
  const countryName = COUNTRIES.find(c => c.code === countryCode)?.name;
  const showFigures = countryCode !== "GB" || vatRegistered;
  const ready = loaded && !loadFailed;

  const subline = ready
    ? `${visible.length} period${visible.length === 1 ? "" : "s"} · ${taxName} filed ${FILING_WORDS[filingPeriod] || filingPeriod}`
    : `${taxName} filing periods, output against input ${taxAbbr}`;

  return (
    <div className="invc-section tax">
      <SectionHead
        title="Tax summary"
        subline={subline}
        action={
          <Button variant="secondary" onClick={() => exportCSV(visible, taxConfig, baseCurrency)} disabled={!ready || !showFigures}>
            Export CSV
          </Button>
        }
      />

      {/* Jurisdiction and range */}
      <div className="invc-bar">
        <div className="invc-bar__filters tax-bar">
          <Select
            aria-label="Tax jurisdiction"
            value={countryOverride || countryCode || ""}
            onChange={e => applyCountryOverride(e.target.value)}
          >
            <option value="">Auto-detect</option>
            {COUNTRIES.map(c => (
              <option key={c.code} value={c.code}>{c.name} ({TAX_CONFIGS[c.code]?.taxAbbr || c.code})</option>
            ))}
          </Select>
          <span className="invc-pill">{sourceText}{countryName ? ` · ${countryName}` : ""}</span>
        </div>
        <Select aria-label="Period range" value={yearsBack} onChange={e => setYearsBack(Number(e.target.value))}>
          <option value={1}>Last 12 months</option>
          <option value={2}>Last 2 years</option>
          <option value={3}>Last 3 years</option>
          <option value={5}>Last 5 years</option>
        </Select>
      </div>
      {taxConfig.thresholdNote && <p className="invc-muted">{taxConfig.thresholdNote}</p>}

      {/* Not VAT registered (item 34: the one setting, in Settings, Branding) */}
      {countryCode === "GB" && !vatRegistered && (
        <EmptyState icon="🧾" title="Not VAT registered">
          Your invoices charge no VAT. If your taxable turnover exceeds {formatMoney(90000, "GBP")} in any 12-month period, you must register for VAT; then turn on VAT registered in Settings, Branding.
        </EmptyState>
      )}

      {/* Figures: only when VAT registered (or non-UK) */}
      {showFigures && (loadFailed ? (
        <ErrorState
          title="Tax figures did not load"
          message="Your invoices could not be loaded, so no tax figures are shown."
          onRetry={refresh}
        />
      ) : !loaded ? (
        <Skeleton lines={4} label="Loading tax summary" />
      ) : (<>
        <section className="invc-kpis" aria-label="Tax totals">
          <StatTile card color="pastel-yellow" label={`Total output ${taxAbbr}`} value={moneyText(totals, pickOutput, baseCurrency)} hint={`${taxAbbr} charged to clients`} />
          {reclaimable && (
            <StatTile card color="pastel-mint" label={`Total input ${taxAbbr}`} value={moneyText(totals, pickInput, baseCurrency)} hint={`${taxAbbr} reclaimable on expenses`} />
          )}
          <StatTile card color="pastel-peach" label={reclaimable ? `Net ${taxAbbr} payable` : `${taxAbbr} collected`} value={moneyText(totals, pickNet, baseCurrency)} hint={reclaimable ? "Output minus input" : "To remit to tax authority"} />
        </section>

        {/* Item 34: output VAT and net PER RATE over the range shown, per currency (decision 72). */}
        <ByRateTable byCur={totals} base={baseCurrency} taxAbbr={taxAbbr} />

        {/* Periods table */}
        {visible.length === 0 ? (
          <EmptyState icon="🧾" title="Nothing to report yet">
            No invoices or {taxAbbr}-bearing expenses found in this period.
          </EmptyState>
        ) : (
          <div className="invc-table-wrap">
            <table className="invc-table tax-table">
              <thead>
                <tr>
                  <th>Period</th>
                  <th className="is-num">Net sales</th>
                  <th className="is-num">Output {taxAbbr}</th>
                  {reclaimable && <th className="is-num">Input {taxAbbr}</th>}
                  <th className="is-num">Net payable</th>
                  <th className="is-num">Invoices</th>
                  <th><span className="tax-sr">Actions</span></th>
                </tr>
              </thead>
              <tbody>
                {visible.map(p => (
                  <tr key={p.key}>
                    <td>
                      <span className="tax-period">{p.label}</span>
                      <span className="invc-small tax-period__dates">{fmtDate(p.start)} to {fmtDate(p.end)}</span>
                    </td>
                    <td className="is-num"><MoneyLines byCur={p.byCur} pick={pickTaxable} base={baseCurrency} /></td>
                    <td className="is-num"><MoneyLines byCur={p.byCur} pick={pickOutput} base={baseCurrency} /></td>
                    {reclaimable && <td className="is-num"><MoneyLines byCur={p.byCur} pick={pickInput} base={baseCurrency} /></td>}
                    <td className="is-num invc-money"><MoneyLines byCur={p.byCur} pick={pickNet} base={baseCurrency} /></td>
                    <td className="is-num">{p.invoiceCount}</td>
                    <td className="is-num">
                      {hasTax(p.byCur) && (
                        <Button variant="secondary" size="sm" onClick={() => setSelectedPeriod(p)} aria-label={`Details for ${p.label}`}>
                          Details
                        </Button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
              <tfoot>
                <tr>
                  <td>Totals</td>
                  <td className="is-num"><MoneyLines byCur={totals} pick={pickTaxable} base={baseCurrency} /></td>
                  <td className="is-num"><MoneyLines byCur={totals} pick={pickOutput} base={baseCurrency} /></td>
                  {reclaimable && <td className="is-num"><MoneyLines byCur={totals} pick={pickInput} base={baseCurrency} /></td>}
                  <td className="is-num invc-money"><MoneyLines byCur={totals} pick={pickNet} base={baseCurrency} /></td>
                  <td className="is-num">{invoiceCount}</td>
                  <td />
                </tr>
              </tfoot>
            </table>
          </div>
        )}

        {/* Disclaimer */}
        {taxConfig.disclaimer && <p className="invc-small tax-disclaimer">{taxConfig.disclaimer}</p>}
      </>))}

      {/* Detail modal */}
      {selectedPeriod && (
        <PeriodDetailModal
          period={selectedPeriod}
          invoices={allInvoices}
          expenses={allExpenses}
          taxConfig={taxConfig}
          base={baseCurrency}
          onClose={() => setSelectedPeriod(null)}
        />
      )}
    </div>
  );
}
